import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ScripsIcon, type ScripsIconName } from '@/shared/ui/components';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * LocationSelector — chip row of organization locations.
 * Mirrors Flutter create_appointment_widget.dart:634–985 (no-provider state):
 *   - Pulls locations from the org-locations query (Flutter sources from
 *     `userData.organizationLocations`).
 *   - Default-selects the first location on mount when nothing is set
 *     (calendar_screen.dart:2684).
 *   - Icon mapping uses the location's `name` exactly (case-sensitive
 *     "Office" / "Home" / "Virtual"; everything else → MapPin).
 */
export function LocationSelector() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const selected = watch('location');

  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: locations = [] } = useOrgLocations(orgId);

  useEffect(() => {
    if (!selected && locations.length > 0) {
      const first = locations[0];
      setValue('location', { id: first.id, name: first.name }, { shouldValidate: true });
    }
  }, [locations, selected, setValue]);

  if (locations.length === 0) {
    return (
      <div className="px-6 py-4">
        <label className="text-xs font-medium text-text-muted">
          Location
        </label>
        <p className="text-sm text-text-hint mt-2">No locations configured.</p>
      </div>
    );
  }

  // When the list fits in the panel width (≤3) we share space with
  // `flex-1` so all tiles render without a horizontal scroll. With 4+
  // locations we fall back to the fixed-width + scroll layout to keep
  // each tile readable.
  const fitsWithoutScroll = locations.length <= 3;

  return (
    <div className="px-6 py-4">
      <label className="text-xs font-medium text-text-muted">
        Location
      </label>
      <div
        className={cn(
          'flex gap-2 mt-2',
          fitsWithoutScroll ? '' : 'overflow-x-auto scrollbar-hide pb-1',
        )}
      >
        {locations.map((loc) => {
          const icon = iconForName(loc.name);
          const hue = LOCATION_HUE[loc.name] ?? 'jade';
          const isSelected = selected?.id === loc.id;
          return (
            <button
              key={loc.id}
              type="button"
              onClick={() => {
                setValue('location', { id: loc.id, name: loc.name }, { shouldValidate: true })
                // Changing location invalidates the selected profile (profiles
                // are filtered by location type). Clear downstream fields so the
                // user picks a profile that matches the new location.
                setValue('appointmentProfileId', null, { shouldValidate: true })
                setValue('appointmentProfileName', null)
                setValue('appointmentProfileColor', null)
                setValue('slotId', null, { shouldValidate: true })
                setValue('slotStart', null, { shouldValidate: true })
                setValue('slotEnd', null, { shouldValidate: true })
              }}
              className={cn(
                'flex items-center gap-2 h-[64px] px-3 rounded-lg border transition-colors',
                fitsWithoutScroll
                  ? 'flex-1 min-w-0'
                  : 'flex-shrink-0 min-w-[120px]',
                isSelected
                  ? ''
                  : 'bg-surface-card border-border-default text-text-muted hover:border-border-strong',
              )}
              style={isSelected ? categoryStyle(loc.name) : undefined}
            >
              <ScripsIcon name={icon} size={16} style={{ color: isSelected ? `var(--cat-${hue}-600)` : undefined }} />
              <span className="text-sm font-semibold truncate">{LOCATION_LABEL[loc.name] ?? loc.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Category hues — same map as QuickBookChip's LOCATION_HUE (the practitioner hover cards). */
const LOCATION_HUE: Record<string, string> = { Office: 'jade', Virtual: 'sky', Home: 'orange', Block: 'purple' };
/** Display labels — same as QuickBookChip's LOCATION_LABEL. */
const LOCATION_LABEL: Record<string, string> = { Office: 'In clinic', Virtual: 'Video', Home: 'Home', Block: 'Block' };

function categoryStyle(name: string): React.CSSProperties {
  const hue = LOCATION_HUE[name] ?? 'jade';
  return {
    background: `var(--cat-${hue}-100)`,
    borderColor: `var(--cat-${hue}-500)`,
    color: `var(--cat-${hue}-800)`,
  };
}

/** Same icon map as QuickBookChip's LOCATION_ICONS. */
const LOCATION_ICONS: Record<string, ScripsIconName> = { Office: 'office', Virtual: 'virtual', Home: 'home', Block: 'block' };

function iconForName(name: string): ScripsIconName {
  return LOCATION_ICONS[name] ?? 'office';
}

